package com.iregained.site.components.sections.contact.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.contact.styles.*
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.AlignSelf
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.fetch.NO_CORS
import org.w3c.fetch.RequestInit
import kotlin.js.json

external fun encodeURIComponent(str: String): String

// UNIQUE ANIMATION: Split Ascend
@Composable
private fun Modifier.splitAscend(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 40.px).transition(
        Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun ContactB2bDemoSection(data: ContactB2bDemoData = defaultContactB2bData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "contact-b2b-demo-section"
    var isVisible by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()

    val ctx = com.varabyte.kobweb.core.rememberPageContext()
    val intent = ctx.route.params["intent"]
    val initialFacilityType = if (intent == "distributor") "Distributor" else ""
    var formSubmitted by remember { mutableStateOf(false) }
    var isSubmitting by remember { mutableStateOf(false) }

    if (data.title.isNotBlank()) {
        Column(
            modifier = ContactB2bContainerStyle.toModifier().fillMaxWidth().id(targetId)
            .background(palette.custom.lightGray).styleModifier { property("scroll-margin-top", "100px") }
            .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent),
            horizontalAlignment = Alignment.CenterHorizontally) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SimpleGrid(numColumns(1, lg = 2), modifier = ContactB2bGridStyle.toModifier()) {

                        Column(
                            modifier = LogisticsColumnStyle.toModifier().splitAscend(isVisible, 0)
                                .alignSelf(AlignSelf.Center)
                        ) {
                            SpanText(text = data.title, modifier = LogisticsTitleStyle.toModifier())
                            if (data.description.isNotBlank()) Div(
                                attrs = LogisticsDescStyle.toModifier().toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.description; onDispose {}
                                    }
                                })
                            data.timelineSteps.forEachIndexed { index, step ->
                                key(step.title) {
                                    Row(
                                        modifier = TimelineStepRowStyle.toModifier(), verticalAlignment = Alignment.Top
                                    ) {
                                        Box(
                                            modifier = TimelineNumberCircleStyle.toModifier(),
                                            contentAlignment = Alignment.Center
                                        ) { Text("${index + 1}") }
                                        Column {
                                            if (step.title.isNotBlank()) SpanText(
                                                text = step.title, modifier = TimelineStepTitleStyle.toModifier()
                                            )
                                            if (step.description.isNotBlank()) Div(
                                                attrs = TimelineStepDescStyle.toModifier().toAttrs {
                                                    ref { htmlElement ->
                                                        htmlElement.innerHTML = step.description; onDispose {}
                                                    }
                                                })
                                        }
                                    }
                                }
                            }
                        }

                        Box(modifier = FormCardStyle.toModifier().splitAscend(isVisible, 200)) {
                            Form(attrs = Modifier.fillMaxWidth().styleModifier { property("color-scheme", "light") }
                                .toAttrs {
                                    attr("action", data.formProcessingEndpoint); attr("method", "POST")
                                    onSubmit { e ->
                                        e.preventDefault()
                                        isSubmitting = true

                                        // 1. Grab the honeypot value
                                        val honeypot =
                                            (document.getElementById("b2b_website_url_honeypot") as? HTMLInputElement)?.value
                                                ?: ""

                                        // 2. (Optional) Fast-fail on frontend to save network calls
                                        if (honeypot.isNotEmpty()) {
                                            isSubmitting = false
                                            formSubmitted = true
                                            return@onSubmit
                                        }

                                        val fullName =
                                            (document.getElementById("b2b_full_name") as? HTMLInputElement)?.value ?: ""
                                        val workEmail =
                                            (document.getElementById("b2b_work_email") as? HTMLInputElement)?.value
                                                ?: ""
                                        val facilityType =
                                            (document.getElementById("b2b_facility_type") as? HTMLSelectElement)?.value
                                                ?: ""
                                        val patientVolume =
                                            (document.getElementById("b2b_patient_volume") as? HTMLSelectElement)?.value
                                                ?: ""
                                        val facilityLocation =
                                            (document.getElementById("b2b_facility_location") as? HTMLInputElement)?.value
                                                ?: ""
                                        val consentGiven =
                                            (document.getElementById("b2b_consent") as? HTMLInputElement)?.checked
                                                ?: false

                                        // 3. Append the honeypot to formData so the backend catches direct-POST bots
                                        val formData =
                                            "form_id=b2b_demo" + "&website_url_honeypot=${encodeURIComponent(honeypot)}" + "&full_name=${
                                                encodeURIComponent(fullName)
                                            }" + "&work_email=${encodeURIComponent(workEmail)}" + "&facility_type=${
                                                encodeURIComponent(
                                                    facilityType
                                                )
                                            }" + "&patient_volume=${encodeURIComponent(patientVolume)}" + "&facility_location=${
                                                encodeURIComponent(
                                                    facilityLocation
                                                )
                                            }" + "&consent=${consentGiven}"

                                        val requestInit = RequestInit(
                                            method = "POST",
                                            mode = org.w3c.fetch.RequestMode.NO_CORS,
                                            headers = json("Content-Type" to "application/x-www-form-urlencoded"),
                                            body = formData
                                        )
                                        window.fetch(data.formProcessingEndpoint, requestInit).then {
                                            isSubmitting = false; formSubmitted = true
                                            (document.getElementById("b2b_full_name") as? HTMLInputElement)?.value =
                                                ""; (document.getElementById("b2b_work_email") as? HTMLInputElement)?.value =
                                            ""; (document.getElementById("b2b_facility_type") as? HTMLSelectElement)?.value =
                                            ""; (document.getElementById("b2b_patient_volume") as? HTMLSelectElement)?.value =
                                            ""; (document.getElementById("b2b_facility_location") as? HTMLInputElement)?.value =
                                            ""
                                            (document.getElementById("b2b_consent") as? HTMLInputElement)?.checked =
                                                false
                                        }.catch { isSubmitting = false; window.alert(data.alertErrorMessage) }
                                    }
                                }) {
                                Input(
                                    type = InputType.Text,
                                    attrs = Modifier.opacity(0).position(Position.Absolute).left((-9999).px) // Move entirely off-screen
                                        .top((-9999).px)
                                        .styleModifier {
                                            property("pointer-events", "none")
                                        }
                                        .width(1.px).height(1.px).styleModifier {
                                            property("pointer-events", "none")
                                        }.toAttrs {
                                            attr("name", "name")
                                            attr("id", "id")
                                            attr("tabindex", "-1")
                                            attr("autocomplete", "off")
                                        })

                                Label(attrs = FormLabelStyle.toModifier().toAttrs()) { Text(data.fullNameLabel) }
                                Input(
                                    type = InputType.Text, attrs = FormInputStyle.toModifier().toAttrs {
                                        placeholder(data.fullNamePlaceholder); attr(
                                        "name", "full_name"
                                    ); attr("id", "b2b_full_name"); attr("required", "true")
                                    })
                                Label(attrs = FormLabelStyle.toModifier().toAttrs()) { Text(data.workEmailLabel) }
                                Input(
                                    type = InputType.Email, attrs = FormInputStyle.toModifier().toAttrs {
                                        placeholder(data.workEmailPlaceholder); attr(
                                        "name", "work_email"
                                    ); attr("id", "b2b_work_email"); attr("required", "true")
                                    })
                                Label(attrs = FormLabelStyle.toModifier().toAttrs()) { Text(data.facilityTypeLabel) }
                                Select(
                                    attrs = FormInputStyle.toModifier().toAttrs {
                                        attr("name", "facility_type"); attr(
                                        "id", "b2b_facility_type"
                                    ); attr("required", "true")
                                    }) {
                                    Option(
                                        value = "",
                                        attrs = { disabled(); selected() }) { Text(data.facilityTypePlaceholder) }
                                    data.facilityTypeOptions.forEach { opt ->
                                        key(opt.value) {
                                            Option(value = opt.value) {
                                                Text(
                                                    opt.label
                                                )
                                            }
                                        }
                                    }
                                }
                                Label(attrs = FormLabelStyle.toModifier().toAttrs()) { Text(data.patientVolumeLabel) }
                                Select(
                                    attrs = FormInputStyle.toModifier().toAttrs {
                                        attr("name", "patient_volume"); attr(
                                        "id", "b2b_patient_volume"
                                    ); attr("required", "true")
                                    }) {
                                    Option(
                                        value = "",
                                        attrs = { disabled(); selected() }) { Text(data.patientVolumePlaceholder) }
                                    data.patientVolumeOptions.forEach { opt ->
                                        key(opt.value) {
                                            Option(value = opt.value) {
                                                Text(
                                                    opt.label
                                                )
                                            }
                                        }
                                    }
                                }
                                Label(
                                    attrs = FormLabelStyle.toModifier().toAttrs()
                                ) { Text(data.facilityLocationLabel) }
                                Input(
                                    type = InputType.Text,
                                    attrs = FormInputStyle.toModifier().toAttrs {
                                        placeholder(data.facilityLocationPlaceholder); attr(
                                        "name", "facility_location"
                                    ); attr("id", "b2b_facility_location"); attr("required", "true")
                                    },
                                )

                                // ADD THIS ENLARGED CHECKBOX BLOCK HERE
                                Row(
                                    modifier = Modifier.fillMaxWidth().margin(bottom = 32.px, top = 16.px),
                                    verticalAlignment = Alignment.Top
                                ) {
                                    Input(
                                        type = InputType.Checkbox,
                                        attrs = Modifier.size(20.px).margin(top = 0.px, right = 12.px)
                                            .cursor(Cursor.Pointer).styleModifier {
                                                property("transform", "scale(1.2)")
                                                property("transform-origin", "top left")
                                            }.toAttrs {
                                                attr("id", "b2b_consent")
                                                attr("name", "consent")
                                                attr("required", "true")
                                            })
                                    Label(
                                        attrs = Modifier.cursor(Cursor.Pointer).lineHeight(1.5).fontSize(14.px)
                                            .color(Color.rgb(0x666666)).toAttrs {
                                                attr("for", "b2b_consent")
                                            }) {
                                        Text(data.consentText)
                                    }
                                }

                                if (data.buttonText.isNotBlank()) {
                                    Button(
                                        attrs = FormSubmitButtonStyle.toModifier()
                                            .opacity(if (isSubmitting) 0.6f else 1f)
                                            .cursor(if (isSubmitting) Cursor.NotAllowed else Cursor.Pointer).toAttrs {
                                                attr("type", "submit"); if (isSubmitting) attr(
                                                "disabled", "true"
                                            )
                                            }) {
                                        Text(if (isSubmitting) data.buttonSubmittingText else data.buttonText)
                                    }
                                }
                            }
                            if (formSubmitted) {
                                Box(
                                    modifier = Modifier.position(Position.Fixed).top(0.px).left(0.px).fillMaxSize()
                                        .zIndex(1000).backgroundColor(Color.argb(0.6f, 0f, 0f, 0f))
                                        .onClick { formSubmitted = false }, contentAlignment = Alignment.Center
                                ) {
                                    Column(
                                        modifier = Modifier.width(90.percent).maxWidth(500.px)
                                            .backgroundColor(Colors.White).borderRadius(8.px).padding(32.px)
                                            .boxShadow(0.px, 10.px, 30.px, color = Color.argb(0.3f, 0f, 0f, 0f))
                                            .onClick { it.stopPropagation() },
                                        horizontalAlignment = Alignment.CenterHorizontally
                                    ) {
                                        Box(
                                            modifier = Modifier.size(64.px).borderRadius(50.percent)
                                                .backgroundColor(Color.rgb(0xE8F5E9)).margin(bottom = 16.px),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            SpanText(
                                                text = data.modalIconText,
                                                modifier = Modifier.color(Color.rgb(0x2E8B57)).fontSize(32.px)
                                                    .fontWeight(FontWeight.Bold)
                                            )
                                        }
                                        SpanText(
                                            text = data.modalTitle,
                                            modifier = Modifier.fontSize(24.px).fontWeight(FontWeight.Bold)
                                                .color(Color.rgb(0x333333)).margin(bottom = 8.px)
                                        )
                                        SpanText(
                                            text = data.modalDescription,
                                            modifier = Modifier.fontSize(16.px).lineHeight(1.5)
                                                .textAlign(TextAlign.Center).color(Color.rgb(0x666666))
                                                .margin(bottom = 24.px)
                                        )
                                        Button(
                                            attrs = FormSubmitButtonStyle.toModifier().fontSize(18.px)
                                                .padding(topBottom = 10.px, leftRight = 24.px).toAttrs {
                                                    onClick {
                                                        formSubmitted = false
                                                    }
                                                }) { Text(data.modalCloseButtonText) }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL
// ==========================================
data class TimelineStepData(val title: String, val description: String)
data class SelectOptionData(val value: String, val label: String)

data class ContactB2bDemoData(
    val sectionId: String? = null,
    val title: String = "Request a System Walkthrough",
    val description: String = "We know integrating the physical MyHand<sup>®</sup> System into a busy clinic is a major decision. Our evaluation process is designed to respect your time and ensure a perfect fit. We will help you understand patient outcomes, financial ROI, and seamless workflow integration.",
    val timelineSteps: List<TimelineStepData> = listOf(
        TimelineStepData(
            title = "Virtual Walkthrough",
            description = "A 30-minute live video demonstration. We will walk you through the physical hardware and the clinical software dashboard. You will see firsthand how patient progress is tracked in real-time."
        ), TimelineStepData(
            title = "Facility Evaluation",
            description = "We discuss your specific spatial requirements, patient volume, and integration goals to customize an implementation plan. This includes a tailored timeline to maximize your clinic's efficiency and patient throughput."
        ), TimelineStepData(
            title = "On-Site Trial & Deployment",
            description = "Experience plug-and-play shipping, installation, and comprehensive CEU-eligible staff training directly in your facility. Your therapists will be fully equipped to begin patient sessions from day one."
        )
    ),

    // Parameterized Form Copy
    val fullNameLabel: String = "Full Name",
    val fullNamePlaceholder: String = "Dr. Jane Smith",
    val workEmailLabel: String = "Work Email",
    val workEmailPlaceholder: String = "jane.smith@hospital.org",

    val facilityTypeLabel: String = "Facility Type",
    val facilityTypePlaceholder: String = "Select facility type",

    val facilityTypeOptions: List<SelectOptionData> = listOf(
        SelectOptionData("Inpatient", "Inpatient Rehabilitation Hospital"),
        SelectOptionData("Outpatient", "Outpatient Clinic"),
        SelectOptionData("University", "University / Research Center"),
        SelectOptionData("Distributor", "Distributor / Commercial Partner"), // New Option
        SelectOptionData("Other", "Other")
    ),

    val patientVolumeLabel: String = "Estimated hand rehabilitation patient volume (monthly)",
    val patientVolumePlaceholder: String = "Select volume...",
    val patientVolumeOptions: List<SelectOptionData> = listOf(
        SelectOptionData("1-20", "1 - 20 Patients"),
        SelectOptionData("21-50", "21 - 50 Patients"),
        SelectOptionData("51+", "50+ Patients")
    ),

    val facilityLocationLabel: String = "Facility Location",
    val facilityLocationPlaceholder: String = "City, State / Province",
    val consentText: String = "I consent to IRegained Inc. collecting my contact information to communicate with me regarding a MyHand® System walkthrough or commercial partnership.",

    // UI Feedback Strings
    val buttonText: String = "Submit Request",
    val buttonSubmittingText: String = "Submitting Request...",
    val alertErrorMessage: String = "Submission connection failed. Please attempt your submission again.",

    // Modal Copy
    val modalIconText: String = "✓",
    val modalTitle: String = "Submission Received",
    val modalDescription: String = "Thank you for requesting a system walkthrough. Our implementation logistics team will review your spatial qualifications and respond within one business day.",
    val modalCloseButtonText: String = "Close",

    val formProcessingEndpoint: String = "https://script.google.com/a/macros/iregained.rehab/s/AKfycbxGjRZicaotnihoNv7zquaM-Pt2mzYPURWqIT-eeCsqwUow8Hvlw-rq1ih7FNswyrx3/exec"
)

val defaultContactB2bData = ContactB2bDemoData()